<script lang="ts" setup>
import dayjs from 'dayjs';
import { Check, ChevronUp } from 'lucide-vue-next';
import { getAverageTime } from '~/api/projects';

const projectWidgetStore = useProjectWidgetStore();

const { data: averageTime } = await useAsyncData(() => getAverageTime(), { default: () => ({ result: 50 }) });
const now = ref(dayjs.utc(projectWidgetStore.project?.now));
const startDate = computed(() => dayjs.utc(projectWidgetStore.project?.project.created_at));
const targetDate = computed(() => startDate.value?.add(averageTime.value.result, 'seconds'));
const totalDuration = computed(() => targetDate.value?.diff(startDate.value));
const elapsedDuration = computed(() => now.value.diff(startDate.value));
const progress = computed(() => Number(Math.min(100, Math.max(0, (elapsedDuration.value / totalDuration.value) * 100)).toFixed(0)));
const projectUpdateInterval = ref();
const timeUpdateInterval = ref();

const updateTime = () => {
  now.value = now.value.add(1, 'second');
  if (progress.value === 100) onGenerateEnd();
};

const onGenerateEnd = () => {
  clearInterval(projectUpdateInterval.value);
};

const showSuccess = computed(
  () =>
    projectWidgetStore.project?.status === 'success' &&
    now.value.diff(dayjs.utc(projectWidgetStore.project.project.created_at), 'minutes') < 2
);

watch(
  () => projectWidgetStore.project,
  () => {
    if (projectWidgetStore.project) now.value = dayjs.utc(projectWidgetStore.project?.now);
  }
);

onMounted(async () => {
  await projectWidgetStore.updateProject();
  updateTime();
  if (projectWidgetStore.project?.status === 'pending') {
    projectUpdateInterval.value = setInterval(projectWidgetStore.updateProject, 1000 * 10);
    useJivoStore().bottomOffset = 90;
  }
  timeUpdateInterval.value = setInterval(updateTime, 1000);
});

onBeforeUnmount(() => {
  clearInterval(projectUpdateInterval.value);
  clearInterval(timeUpdateInterval.value);
});
</script>

<template>
  <client-only v-if="projectWidgetStore.project">
    <div
      v-if="projectWidgetStore.project?.status === 'pending'"
      class="generates-widget"
      @click="navigateTo(`/projects/${projectWidgetStore.project?.project.id}`)"
    >
      <div class="progress-widget-content">
        <span>Проект генерируется</span>
        <div class="progress-bar-container">
          <div class="progress-bar" />
          <span class="progress-bar-text">{{ progress }}%</span>
        </div>
      </div>
      <ChevronUp style="rotate: 90deg" />
    </div>
    <div
      v-else-if="showSuccess"
      class="success-toast"
    >
      <div class="success-toast-content">
        <Check />
        <p>Проект готов</p>
      </div>
      <v-button
        small
        class="success-toast-button"
        @click="navigateTo(`/projects/${projectWidgetStore.project?.project.id}`)"
      >
        Открыть проект
      </v-button>
    </div>
  </client-only>
</template>

<style lang="scss">
.generates-widget {
  position: fixed;
  top: 40px;
  right: 50%;
  transform: translateX(50%);
  z-index: 1000;
  padding: 16px;
  border-radius: 16px;
  background: $background-white;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  user-select: none;
  gap: 16px;
  width: 492px;

  @include media-breakpoint-down(md) {
    bottom: 0px;
    top: unset;
    width: 100%;
    border-radius: 16px 16px 0 0;
    align-items: flex-start;
    gap: 32px;
  }

  // @include media-breakpoint-down(sm) {
  //   top: 64px;

  //   z-index: 100;
  // }

  .progress-widget-content {
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 40px;
    width: 100%;
    white-space: nowrap;

    @include media-breakpoint-down(md) {
      justify-content: space-between;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      white-space: unset;
    }

    .progress-bar {
      height: 12px;
      width: 144px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 16px;
      font-size: 16px;
      font-weight: 600;
      line-height: 40px;
      background: $background-white;
      color: $foreground-contrast;
      box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.05);
      width: 100%;

      background: url('/images/new/loader-figure.svg') rgba(#bebaff, 0.25);
      animation: bg-slide 30s linear infinite;
    }

    .progress-bar-container {
      display: flex;
      flex-direction: row;
      gap: 16px;
      align-items: center;
      width: 100%;
    }

    .progress-bar-text {
      font-size: 16px;
      font-weight: 500;
      color: $foreground-theme;
      white-space: nowrap;
    }
  }
}

.success-toast {
  position: fixed;
  top: 40px;
  right: 50%;
  transform: translateX(50%);
  z-index: 1000;
  padding: 16px;
  border-radius: 16px;
  font-size: 16px;
  background: #c6f1dc;
  color: #177243;
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  user-select: none;

  @include media-breakpoint-down(md) {
    bottom: 0px;
    top: unset;
    border-radius: 16px 16px 0 0;
    z-index: 100;
    gap: 12px;
    width: 100%;
  }

  .success-toast-button {
    background: #e7f9f0;
    color: #177243;
    transition: opacity 0.2s ease-in-out;
    &:hover {
      opacity: 0.8;
    }
  }

  .success-toast-content {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }
}
</style>
